import { useAnimationFrame } from 'framer-motion';
import { camelCase, startCase } from 'lodash';
import { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AutoFullwidth } from 'src/components/auto-fullwidth';
import { Container } from 'src/components/container';
import {
  layoutMainPaddingBottom,
  layoutMainPaddingTop,
} from 'src/components/layout';
import { PageSpinner } from 'src/components/spinner-container';
import { useHeaderHeight } from 'src/hooks/use-header-height';
import { usePrevious } from 'src/hooks/use-previous';
import { useTitle } from 'src/hooks/use-title';

const offset = '10px';

const isRootHash = (hash: string) => hash === '' || hash === '#/';

const Page: FC = () => {
  const { hash, pathname } = useLocation();
  const isAngular = pathname === '/platform/' || pathname === '/platform';

  const history = useHistory();
  const spacerRef = useRef<HTMLDivElement>(null);
  const header = useHeaderHeight();
  const [iframe] = useState(() => {
    const [iframe] = document.getElementsByName('embedded-angular');
    return iframe as HTMLIFrameElement;
  });

  // the iframe will always be available at this point, but the angular app
  // might not be fully initialized. this happens if you build both SPAs locally
  // and the React app is already available, but Angular is not.
  // but I assume it could even happen in production, in case the network is flaky
  // and it takes a while to properly load the Angular code.
  // first we check, if the Angular app already set the `__ANGULAR_APP_LOADED__` flag.
  // this happens in angular/src/main/webapp/app/app.module.ts.
  // if yes, everything is fine.
  // if not, we need to re-check this in our useAnimationFrame.
  // only if the app is fully loaded, we're able to sync URLs and content height.
  const [loaded, setLoaded] = useState(
    Boolean((iframe.contentWindow as any)?.__ANGULAR_APP_LOADED__)
  );

  // needed for initial mounting
  const [initialHash] = useState(() => (isRootHash(hash) ? '#/start' : hash));

  // handle initial mounting in root
  useLayoutEffect(() => {
    if (!isAngular) return;
    if (!isRootHash(hash)) return;
    history.replace({ hash: '#/start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAngular]);

  // sync angular with url changes
  useLayoutEffect(() => {
    if (!loaded) return;

    if (hash.startsWith('#/react')) {
      history.replace(decodeURIComponent(hash.replace('#/react', '')));
    } else {
      const newHash = hash === '#/initialize' ? initialHash : hash;
      const usedHash = isAngular ? newHash : '#/loading';

      // for some reasons "iframe.contentWindow?.history.replaceState" only worked properly initially
      // and for regular links, but not always if the "$location" API was used. (this also only
      // happened in a production build, not locally.)
      // therefor this now a fallback (mostly used only for the initial loading) and we prefer "__angularLocation"
      // when it's available. "__angularLocation" is just "$location" made globally available.
      if ((iframe.contentWindow as any)?.__angularLocation) {
        const [path, search] = usedHash.replace('#', '').split('?');
        (iframe.contentWindow as any)?.__angularLocation
          .path(path)
          .search(search ?? '')
          .replace();
      } else {
        iframe.contentWindow?.history.replaceState(null, '', usedHash);
      }

      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, isAngular, hash, iframe, initialHash]);

  // sync content height with iframe height
  // (I previously tried MutationObserver together with window.addEventListener('resize'), but
  // requestAnimationFrame was more reliable and straight forward)
  useAnimationFrame(() => {
    if (!loaded) {
      // check if the iframe is loaded by now
      const updatedLoaded = Boolean(
        (iframe.contentWindow as any)?.__ANGULAR_APP_LOADED__
      );
      if (updatedLoaded) {
        setLoaded(true);
      } else {
        return;
      }
    }

    const { contentWindow } = iframe;
    if (!contentWindow) return;

    // not sure when this happens, but "body" can actually be null
    if (!contentWindow.document.body) return;

    // fyi: safari still reports a scroll height even if the iframe is not visible
    const height = `calc(${contentWindow.document.body.scrollHeight}px + ${offset})`;
    iframe.style.height = height;
    if (spacerRef.current)
      spacerRef.current.style.height = isAngular ? height : '0px';
  });

  // update iframe styles on ever render
  iframe.style.display = isAngular ? 'block' : 'none';
  iframe.style.top = `${header.height}px`;

  return (
    <>
      {isAngular && !loaded && (
        <Container>
          <PageSpinner />
        </Container>
      )}
      {isAngular && hash.startsWith('#/reporting') && <AutoFullwidth />}
      {isAngular && <Title hash={hash} />}
      <div
        ref={spacerRef}
        style={
          isAngular
            ? {
                marginBottom: `calc(-${layoutMainPaddingTop} - ${layoutMainPaddingBottom})`,
              }
            : undefined
        }
      />
    </>
  );
};

const Title: FC<{ hash: string }> = ({ hash }) => {
  // generate stupid, but nicer title then the old static "PRISMA"
  const title = useMemo(() => startCase(camelCase(hash)), [hash]);
  const prevTitle = usePrevious(title);
  useTitle(hash.startsWith('#/react') ? prevTitle : title);

  return null;
};

export default Page;
